import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC6 } from "@/utils/indicatorsEsf";
import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { DetailedCitizenScoresIndicatorC6Data } from "@/services/esus/indicator/esf/c6/getDetailedCitizensScoresIndicatorC6";

import { IndicatorEsfC6CitizensScoresFilters } from "./IndicatorEsfC6CitizensScoresFilters";
import { IndicatorEsfC6CitizensScoresTable } from "./IndicatorEsfC6CitizensScoresTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC6CitizensScoresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensScores: Paginated<DetailedCitizenScoresIndicatorC6Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensScoresIndicatorC6: () => Promise<
		DetailedCitizenScoresIndicatorC6Data[]
	>;
};

export function IndicatorEsfC6CitizensScores({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensScores,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensScoresIndicatorC6,
}: Readonly<IndicatorEsfC6CitizensScoresProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const citizensScores = await handleGetAllDetailedCitizensScoresIndicatorC6();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(citizensScores).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC6CitizensScoresToGenericObjectArray(citizensScores);
			exportExcel(
				excelData,
				`Pontuações de cidadãos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC6CitizensScoresToGenericObjectArray(
		citizensScores: DetailedCitizenScoresIndicatorC6Data[]
	) {
		return citizensScores.map(
			(citizenScores) =>
				({
					Cidadão: citizenScores.fullName,
					"Data de Nascimento": dayjs(citizenScores.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenScores.birthdate),
					Documento: citizenScores.cpf ?? citizenScores.cns ?? "",
					Area: citizenScores.area ?? "FA",
					"Ine Equipe": citizenScores.healthTeam?.ine ?? "",
					"Nome Equipe": citizenScores.healthTeam?.name ?? "",
					"Status última Consulta": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenScores.lastAttendanceStatus
					),
					"Status 2 registros peso e altura": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenScores.twoWeightAndHeightMetricsRegistersStatus
					),
					"Status 2 visitas": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenScores.twoHomeVisitsStatus
					),
					"Status vacina influenza": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenScores.vaccinationStatus
					),
					Pontos: citizenScores.score,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC6CitizensScoresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC6CitizensScoresTable
							citizensScores={paginatedCitizensScores.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensScores?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensScores.pageSize}
								totalPages={paginatedCitizensScores.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
